import React, { useState, useMemo, useEffect } from 'react';
import { FormControl, Select, MenuItem } from '@mui/material';
import ProductSelector from './ProductSelector';
import PostAnalytics from './PostAnalytics';
import PostEngagementAnalytics from './PostEngagementAnalytics';
import PostSentimentEngagementAnalytics from './PostSentimentEngagementAnalytics';
import YouTubeViewsCommentsChart from './YouTubeViewsCommentsChart';
import { apiPost } from '../services/apiClient';

// YouTube Analytics — v2 ("New"). Like the old page, ONE shared control bar
// (topic + date range + bucket) drives every chart at once. Each chart is the
// Daily Tracker component in `embedded` mode (its own controls hidden), scoped
// to the YouTube source and auto-fetching when the shared inputs change.

const BUCKET_OPTIONS = [
    { value: 'day', label: 'Daily' },
    { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' },
];

const labelStyle = {
    fontSize: 9, fontWeight: 600, color: '#999', textTransform: 'uppercase',
    letterSpacing: '0.5px', marginBottom: 3,
};
const dateStyle = {
    padding: '6px 10px', borderRadius: 4, border: '1px solid #d0d0d0',
    fontSize: 13, height: 36, boxSizing: 'border-box', outline: 'none',
};

const fmt = (n) => (Number(n) || 0).toLocaleString();

// Top Channels — reads the live per-(topic, channel) running totals from
// youtube_channel_topic_stats (via /topic/youtube/channel-stats), sorted by
// total views server-side. These are ALL-TIME cumulative totals (the upsert
// table has no date dimension), so this table is driven by topic only — the
// shared date range does not apply to it.
const TOP_CHANNELS_LIMIT = 15;

const TopChannels = ({ topicId, topicName }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    // Which column drives the server-side top-N selection, and its direction.
    // Most columns are descending-only (top by that metric); NPS is the one
    // that toggles asc/desc (best- vs worst-rated channels). Clicking a
    // different column resets to descending.
    const [sortKey, setSortKey] = useState('total_views');
    const [sortDir, setSortDir] = useState('desc');

    // Re-fetch when the sort column/direction changes so the server returns the
    // TRUE top-N for that metric (not a local re-order of the top-by-views set).
    useEffect(() => {
        if (!topicId) return;
        let active = true;
        (async () => {
            setLoading(true);
            try {
                const resp = await apiPost('topic/youtube/channel-stats', { topic_id: topicId, limit: TOP_CHANNELS_LIMIT, sort: sortKey, dir: sortDir });
                const json = await resp.json();
                if (active) setRows(json.data || []);
            } catch (e) {
                console.error('[TopChannels]', e);
                if (active) setRows([]);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [topicId, sortKey, sortDir]);

    // NPS toggles direction on repeat-click; other columns are descending-only.
    const handleSort = (key) => {
        if (key === 'avg_nps' && sortKey === 'avg_nps') {
            setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
    };

    // Rows arrive already ordered from the server; render as-is.
    const sortArrow = (key) => (sortKey === key ? (sortDir === 'desc' ? ' ▼' : ' ▲') : '');

    const SORT_LABELS = {
        total_views: 'total views',
        num_videos: 'number of videos',
        total_likes: 'total likes',
        total_comments: 'total comments',
        avg_nps: 'average NPS',
    };

    const th = { padding: 10, textAlign: 'right', fontWeight: 600 };
    const sortableTh = { ...th, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };
    const activeTh = { ...sortableTh, color: '#1976d2' };
    const thFor = (key) => (sortKey === key ? activeTh : sortableTh);
    const td = { padding: 10, textAlign: 'right' };

    return (
        <div style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: 20 }}>
            <h3 style={{ margin: '0 0 4px' }}>Top Channels</h3>
            <div style={{ fontSize: 12, color: '#999', marginBottom: 12 }}>
                All-time cumulative totals for {topicName || 'this topic'} · {sortDir === 'asc' ? 'lowest' : 'top'} {TOP_CHANNELS_LIMIT} by {SORT_LABELS[sortKey] || 'total views'}.
                {sortKey === 'avg_nps' && ' NPS ranking includes only channels with 2+ rated videos.'}
                <div style={{ fontStyle: 'italic', marginTop: 2 }}>Click a column header to sort. Click Avg NPS again to flip best/worst.</div>
            </div>
            {loading ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>Loading…</div>
            ) : rows.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
                    No channel data yet for this topic (fills in as YouTube rechecks run).
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                <th style={{ padding: 10, textAlign: 'left' }}>Channel</th>
                                <th style={thFor('num_videos')} onClick={() => handleSort('num_videos')}
                                    title="Sort by number of videos">Videos{sortArrow('num_videos')}</th>
                                <th style={thFor('total_views')} onClick={() => handleSort('total_views')}
                                    title="Sort by total views">Total Views{sortArrow('total_views')}</th>
                                <th style={thFor('total_likes')} onClick={() => handleSort('total_likes')}
                                    title="Sort by total likes">Total Likes{sortArrow('total_likes')}</th>
                                <th style={thFor('total_comments')} onClick={() => handleSort('total_comments')}
                                    title="Sort by total comments">Total Comments{sortArrow('total_comments')}</th>
                                <th style={thFor('avg_nps')} onClick={() => handleSort('avg_nps')}
                                    title="Sort by average NPS (click again to flip best/worst; channels need 2+ rated videos)">Avg NPS{sortArrow('avg_nps')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: 10 }}>{r.channel || 'Unknown'}</td>
                                    <td style={td}>{fmt(r.num_videos)}</td>
                                    <td style={td}>{fmt(r.total_views)}</td>
                                    <td style={td}>{fmt(r.total_likes)}</td>
                                    <td style={td}>{fmt(r.total_comments)}</td>
                                    <td style={td} title={r.rated_videos ? `${r.rated_videos} rated video(s)` : 'no NPS-rated videos'}>
                                        {r.avg_nps != null ? Number(r.avg_nps).toFixed(1) : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const YouTubeAnalyticsNew = ({ topics, companyName, companyTicker }) => {
    const topicMap = useMemo(() => {
        const m = {};
        (topics || []).forEach(t => {
            if (t && t.id) m[t.id] = { id: t.id, name: t.name || t.topic, priority: t.priority };
        });
        return m;
    }, [topics]);

    const today = new Date().toISOString().split('T')[0];
    const [selectedProduct, setSelectedProduct] = useState('');
    const [startDate, setStartDate] = useState('2024-01-01');
    const [endDate, setEndDate] = useState(today);
    const [bucketPeriod, setBucketPeriod] = useState('month');

    // auto-select the highest-priority topic once topics arrive
    useEffect(() => {
        if (selectedProduct || Object.keys(topicMap).length === 0) return;
        const sorted = Object.values(topicMap).sort(
            (a, b) => (a.priority ?? Infinity) - (b.priority ?? Infinity)
        );
        if (sorted[0]) setSelectedProduct(sorted[0].id);
    }, [topicMap, selectedProduct]);

    if (!topics || Object.keys(topicMap).length === 0) {
        return (
            <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
                No topics available.
            </div>
        );
    }

    // Props shared by every embedded chart: same topic, date range and bucket,
    // scoped to the YouTube source. Changing any of these re-fetches all charts.
    const shared = {
        topicMap,
        companyName,
        companyTicker,
        embedded: true,
        sourceFilter: 'youtube',
        selectedProduct,
        onProductChange: setSelectedProduct,
        startDate,
        endDate,
        bucketPeriod,
        // Zero-fill empty buckets (a bar per bucket across the range) and use a
        // shorter plot so several charts stack into view at once — matches the
        // "New" daily tracker look.
        fillEmptyBuckets: true,
        chartHeight: 320,
        // Compact large value-axis ticks (1.2K / 5M) — view counts here run
        // into the millions and otherwise fill the axis with zeroes.
        compactYAxis: true,
    };

    return (
        <div style={{ padding: 20 }}>
            {/* Shared control bar — one input set drives all charts below */}
            <div style={{
                display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', rowGap: 12,
                background: '#fafafa', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 8,
                padding: '16px 20px', marginBottom: 24,
            }}>
                <div style={{ flex: '1 1 auto', minWidth: 140, maxWidth: 240 }}>
                    <div style={labelStyle}>Product</div>
                    <ProductSelector
                        topicMap={topicMap}
                        selectedId={selectedProduct}
                        onSelect={setSelectedProduct}
                        label=""
                        size="small"
                    />
                </div>
                <div>
                    <div style={labelStyle}>Start Date</div>
                    <input type="date" value={startDate} max={endDate}
                        onChange={(e) => setStartDate(e.target.value)} style={dateStyle} />
                </div>
                <div>
                    <div style={labelStyle}>End Date</div>
                    <input type="date" value={endDate} min={startDate} max={today}
                        onChange={(e) => setEndDate(e.target.value)} style={dateStyle} />
                </div>
                <div>
                    <div style={labelStyle}>Bucket</div>
                    <FormControl size="small" sx={{ minWidth: 110 }}>
                        <Select value={bucketPeriod} onChange={(e) => setBucketPeriod(e.target.value)} sx={{ height: 36 }}>
                            {BUCKET_OPTIONS.map(o => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
            </div>

            {/* Charts — all driven by the shared controls above */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                {/* Views + Comments in one chart: views as bars (left axis),
                    comments as a toggleable line on a secondary (right) axis so
                    the far-smaller comments series stays readable. */}
                <YouTubeViewsCommentsChart {...shared} chartTitle="YouTube Views & Comments" />
                {/* Videos published over time — reuses the Post Volume tracker
                    (PostAnalytics) scoped to the youtube_video post type. Counts
                    RELEVANT videos (assessed, NPS > 3) by their PUBLISH date. */}
                <PostAnalytics
                    {...shared}
                    sourceType="youtube_video"
                    chartTitle="YouTube Videos Published"
                    valueAxisLabel="Number of Videos"
                    barName="Videos Published"
                />
                <PostSentimentEngagementAnalytics
                    {...shared}
                    metric="views"
                    chartTitle="YouTube Views by NPS"
                />

                {/* Top Channels — live running totals, all-time, topic-driven */}
                <TopChannels topicId={selectedProduct} topicName={topicMap[selectedProduct]?.name} />
            </div>
        </div>
    );
};

export default YouTubeAnalyticsNew;
